import { create } from 'zustand';
import { api } from '../services/api';

export interface User {
  id: string;
  display_id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'editor' | 'viewer';
  is_active: boolean;
  avatar_url: string | null;
  phone: string | null;
  company: string | null;
  position: string | null;
  bio: string | null;
  tenant_id: string | null;
  // Adressfelder
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  // URLs
  website: string | null;
  linkedin_url: string | null;
  xing_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserStats {
  total: number;
  active: number;
  admins: number;
  editors: number;
  viewers: number;
}

interface UsersState {
  users: User[];
  stats: UserStats | null;
  isLoading: boolean;
  error: string | null;

  fetchUsers: () => Promise<void>;
  fetchStats: () => Promise<void>;
  createUser: (data: { email: string; password: string; name?: string; role?: string }) => Promise<User>;
  updateUser: (id: string, data: Partial<User>) => Promise<void>;
  deactivateUser: (id: string) => Promise<void>;
  reactivateUser: (id: string) => Promise<void>;
  changeUserPassword: (id: string, newPassword: string) => Promise<void>;
  clearError: () => void;
}

export const useUsersStore = create<UsersState>((set) => ({
  users: [],
  stats: null,
  isLoading: false,
  error: null,

  fetchUsers: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ success: boolean; data: User[] }>('/users');
      set({ users: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Benutzer';
      set({ error: message, isLoading: false });
    }
  },

  fetchStats: async () => {
    try {
      const response = await api.get<{ success: boolean; data: UserStats }>('/users/stats');
      set({ stats: response.data });
    } catch (error) {
      console.error('Fehler beim Laden der Statistiken:', error);
    }
  },

  createUser: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ success: boolean; data: User }>('/users', data);
      const newUser = response.data;
      set((state) => ({
        users: [newUser, ...state.users],
        isLoading: false,
      }));
      return newUser;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Erstellen';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateUser: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.put<{ success: boolean; data: User }>(`/users/${id}`, data);
      const updatedUser = response.data;
      set((state) => ({
        users: state.users.map((u) => (u.id === id ? updatedUser : u)),
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Aktualisieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deactivateUser: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await api.post(`/users/${id}/deactivate`);
      set((state) => ({
        users: state.users.map((u) => (u.id === id ? { ...u, is_active: false } : u)),
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Deaktivieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  reactivateUser: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await api.post(`/users/${id}/reactivate`);
      set((state) => ({
        users: state.users.map((u) => (u.id === id ? { ...u, is_active: true } : u)),
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Reaktivieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  changeUserPassword: async (id, newPassword) => {
    set({ isLoading: true, error: null });
    try {
      await api.post(`/users/${id}/change-password`, { newPassword });
      set({ isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Ändern des Passworts';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
